/**
 * The execution plan: which half of a report runs where.
 *
 * Placement is a property of the operation, computed once from the spec and
 * consumed by both sides — the server executes `plan.sql`, the client executes
 * `plan.memory`. Neither decides; both obey. Branching at each call site
 * instead is how the two ends drift, and how adding an operation turns into
 * editing two code paths.
 *
 * See docs/reporting/requirements.md §FR-1.18.
 */

import { activeFilters, canonicalFilters, type FilterNode } from './filter';
import { findMeasure, isAdditive, type ReportModel } from './model';
import {
  DEFAULT_DETAIL_LIMIT,
  DEFAULT_PIVOT_LIMIT,
  isLevelSort,
  MAX_DETAIL_LIMIT,
  type ReportSpec,
  type ShowAsMode,
  type SortSpec,
} from './spec';

export type Stage = 'sql' | 'memory';

export interface AxisSort {
  axis: 'rows' | 'cols';
  sort: SortSpec;
}

export interface QueryPlan {
  /** What the query does. This, and only this, decides whether to re-run it. */
  sql: {
    /**
     * The subject area, and the window it is asked about.
     *
     * On the QUERY half because they are two of the three things every emitted
     * statement binds — a different period is a different answer, so a key
     * built without them would serve July's rows under August's dates.
     */
    model: string;
    from: string;
    to: string;
    /** Rows and columns are ONE `GROUP BY`; the split is presentation. */
    groupBy: string[];
    measures: string[];
    /** DETAIL: the fields selected, in display order. Empty for a pivot. */
    columns: string[];
    /** The conditions that reach the query — paused rules already taken out. */
    filters: FilterNode[];
    /**
     * The `ORDER BY` chain: the first entry decides, the rest break its ties.
     *
     * A LIST rather than one entry because a listing can honour the whole
     * chain — there is no aggregation for a later entry to have been computed
     * after. A pivot still contributes at most one, because everything past
     * the first depends on a total across the columns that no single result
     * row carries.
     */
    orderBy?: SortSpec[];
    limit?: number;
    /** DETAIL only: rows skipped, in the order the query ran. */
    offset?: number;
    /**
     * DETAIL only: whether the query must also total its additive columns.
     *
     * On the QUERY half because that is what it now costs. A pivot's totals are
     * arithmetic over cells already on screen, so toggling one changes nothing
     * about the question; a listing's cannot be — once it pages, the rows to
     * add up are the ones NOT on screen — so the total is another aggregate the
     * statement has to carry. Left off this half, toggling the control would
     * show the old result back with no total in it, because nothing about the
     * query looked different.
     */
    totals?: boolean;
    /**
     * `spec.basis` — the figure a drill landing is the basis of. On the QUERY
     * half because it changes the statement: it lends a measure, and on a
     * listing it drops the rows that carry none of it.
     */
    basis?: { model: string; measure: string };
    /**
     * `spec.origin` — the model a drill chain started on. On the QUERY half for
     * the same reason: it changes which rows the statement may return, so two
     * landings that differ only in where they were drilled from are two
     * questions and must not share a cached answer.
     */
    origin?: string;
  };
  /** What the projection does after the rows arrive. */
  memory: {
    pivot: { rows: string[]; cols: string[] };
    /** Only the sorts SQL could not take. */
    sort: AxisSort[];
    /**
     * The row cap, when the projection owns the order and therefore the cap.
     *
     * A cap belongs wherever the ORDER is decided — taken anywhere else it
     * keeps the wrong members. SQL takes it when SQL sorted; this is the other
     * half, and without it the cap was simply dropped: a spec asking for ten
     * rows rendered every group the query returned.
     */
    limit?: number;
    showAs: Record<string, ShowAsMode>;
    totals: { row: boolean; col: boolean };
  };
  /** Why each step landed where it did. Feeds the SQL disclosure and tests. */
  rationale: { step: string; stage: Stage; because: string }[];
}

/**
 * Decide where every operation runs.
 *
 * The one rule with teeth is the row sort. With no column axis there is exactly
 * one result row per row tuple, so `ORDER BY` is exact — and it composes with
 * the cap, which matters because a cap taken in a different order than the one
 * displayed silently shows the wrong members. With a column axis the row order
 * depends on each row's total ACROSS the columns, which no single result row
 * carries, so it can only be done after the projection.
 */
export function planQuery(model: ReportModel, spec: ReportSpec): QueryPlan {
  if (spec.kind === 'detail') return planDetail(spec);

  const rationale: QueryPlan['rationale'] = [];
  const memorySort: AxisSort[] = [];
  let orderBy: SortSpec | undefined;
  /** Where the SQL head's rationale entry sits, in case it has to move. */
  let headRationale = -1;

  /**
   * A sort can only name the label or a measure this report selects.
   *
   * `validateSpec` refuses anything else outright, but the plan is also built
   * for specs that never went through it — a saved report, a widget's stored
   * settings. Dropping the sort keeps the axis in its natural order instead of
   * handing the compiler an `ORDER BY` for a column it will not select.
   */
  const unresolvable = (sort: SortSpec) =>
    !isLevelSort(sort.by) && !spec.measures.includes(sort.by);

  const pivoted = spec.cols.length > 0;
  for (const sort of spec.rowSort) {
    if (unresolvable(sort)) {
      rationale.push({
        step: 'row sort',
        stage: 'memory',
        because: `this report does not select "${sort.by}", so it cannot order by it`,
      });
      continue;
    }
    const measure = isLevelSort(sort.by) ? undefined : findMeasure(model, sort.by);
    // WHETHER THIS ENTRY HAS TO SUM ANYTHING, which is the only question a
    // non-additive measure cannot survive.
    //
    // A whole-axis entry ranks every level, and the outer ones only exist as a
    // total over the levels beneath them — but that has always been judged by
    // `pivoted` alone, and a saved report is not the place to tighten a rule.
    // A per-level entry is judged exactly: at the innermost level with no
    // column axis, a group IS one result row, so there is nothing to sum and
    // an average or a ratio ranks fine.
    const aggregatesToRank =
      sort.on === undefined
        ? pivoted
        : pivoted || spec.rows.indexOf(sort.on) < spec.rows.length - 1;
    if (aggregatesToRank && measure && !isAdditive(measure)) {
      // Ranking rows by a non-additive measure across columns would mean
      // summing values that cannot be summed. Drop this entry of the chain
      // rather than order by a number that does not exist.
      rationale.push({
        step: 'row sort',
        stage: 'memory',
        because: `${measure.label} is not additive, so it has no total across the columns to rank by`,
      });
    } else if (sort.on !== undefined || sort.by === 'key') {
      // A PER-LEVEL ORDER IS THE PROJECTION'S, always — and so is any order by
      // KEY, per level or not: the compiler's `ORDER BY` speaks labels and
      // measures, and the projection already compares keys.
      //
      // SQL could express some of it — every level by its own label is an
      // `ORDER BY` over the label columns in level order — but not all of it,
      // and a planner that emits half a level chain and finishes the rest in
      // memory is a second code path guarding an invariant (`the cap is taken
      // in the order displayed`) that one path already guarantees. There is
      // nothing to buy with it either: a pivot has no pager and the builder
      // offers no cap, so `spec.limit` is unset and both branches fetch the
      // same DEFAULT_PIVOT_LIMIT ceiling. `orderTuples` ranks each level by
      // that group's own total, which is the thing being asked for.
      memorySort.push({ axis: 'rows', sort });
      rationale.push({
        step: 'row sort',
        stage: 'memory',
        because: 'each level is ranked by its own group totals, which the projection is what knows',
      });
    } else if (pivoted || sort.at?.length) {
      memorySort.push({ axis: 'rows', sort });
      rationale.push({
        step: 'row sort',
        stage: 'memory',
        because: 'ranks by a total across the columns, which no single result row carries',
      });
    } else {
      // Only the FIRST entry can become an ORDER BY; the rest are its
      // tie-breakers and must be applied in the same place.
      if (!orderBy && memorySort.every((s) => s.axis !== 'rows')) {
        orderBy = sort;
        headRationale = rationale.length;
        rationale.push({
          step: 'row sort',
          stage: 'sql',
          because: 'one result row per row tuple, so ORDER BY is exact and composes with the cap',
        });
      } else {
        memorySort.push({ axis: 'rows', sort });
      }
    }
  }

  // A WHOLE-AXIS CHAIN IS APPLIED IN ONE PLACE. Its head went to SQL above,
  // but if any entry without a level landed in memory — a tie-breaker, or an
  // order by key — the projection re-sorts the axis by the memory entries
  // ALONE, and a tie-breaker over unique tuples decides every comparison: the
  // head would be silently discarded. So the head follows it into memory, in
  // front, and the statement falls back to its plain key order.
  if (orderBy && memorySort.some((s) => s.axis === 'rows' && s.sort.on === undefined)) {
    memorySort.unshift({ axis: 'rows', sort: orderBy });
    rationale[headRationale] = {
      step: 'row sort',
      stage: 'memory',
      because: 'the rest of this chain is applied after the query, so its head is too',
    };
    orderBy = undefined;
  }

  for (const sort of spec.colSort) {
    if (unresolvable(sort)) {
      rationale.push({
        step: 'column sort',
        stage: 'memory',
        because: `this report does not select "${sort.by}", so it cannot order by it`,
      });
      continue;
    }
    memorySort.push({ axis: 'cols', sort });
    rationale.push({
      step: 'column sort',
      stage: 'memory',
      because: 'ranks by a total down the rows, which no single result row carries',
    });
  }

  // The invariant: a cap must be taken in the order it will be displayed in.
  // If the row axis is ordered after the fact, SQL must not have truncated it,
  // or the rows shown are the wrong members in the right order.
  const rowsOrderedInMemory = memorySort.some((s) => s.axis === 'rows');
  // The spec's cap goes wherever the ORDER went, because a cap taken in a
  // different order than the one displayed keeps the wrong members. So SQL
  // takes it only when SQL owns the order; otherwise the projection does, and
  // {@link QueryPlan.memory.limit} is what says so. It used to be dropped on
  // that path and applied nowhere, which is why a report asking for ten rows
  // rendered every group it had.
  const memoryLimit = rowsOrderedInMemory ? spec.limit : undefined;
  // The CEILING is a different thing from the cap and applies either way: the
  // alternative on the memory path was no `LIMIT` at all, which is an unbounded
  // statement rather than a cap that moved. It is a safety valve, not a page
  // size — and it is NOT exact. Ranking after a truncated fetch ranks within
  // whatever the ceiling happened to keep, so `truncated` on a memory-ordered
  // axis means "this order is over a partial set", not merely "there is more".
  const limit = Math.min(
    rowsOrderedInMemory ? DEFAULT_PIVOT_LIMIT : (spec.limit ?? DEFAULT_PIVOT_LIMIT),
    DEFAULT_PIVOT_LIMIT,
  );
  // Unconditional. The ceiling applies to every pivot, including the common
  // case of a spec that names no cap of its own — and a cap nobody can see
  // explained is exactly the one a reader needs explained, because it is also
  // the one that can make a ranking wrong.
  rationale.push(
    rowsOrderedInMemory
      ? {
          step: 'row cap',
          stage: 'memory',
          because: `the row order is decided after the query, so capping in SQL would keep the wrong rows; the query still stops at ${DEFAULT_PIVOT_LIMIT} groups, and past that this ranking is over a partial set`,
        }
      : {
          step: 'row cap',
          stage: 'sql',
          because:
            spec.limit === undefined
              ? `no cap was asked for, so the ${DEFAULT_PIVOT_LIMIT}-group ceiling applies`
              : 'SQL owns the order, so the cap is taken in the order that will be displayed',
        },
  );

  for (const step of ['pivot projection', 'showAs', 'totals'] as const) {
    rationale.push({
      step,
      stage: 'memory',
      because: 'derived from cells of this result, which only exist once the projection is known',
    });
  }

  return {
    sql: {
      model: spec.model,
      from: spec.from,
      to: spec.to,
      groupBy: [...spec.rows, ...spec.cols],
      measures: spec.measures,
      columns: [],
      filters: activeFilters(spec.filters),
      ...(orderBy ? { orderBy: [orderBy] } : {}),
      limit,
      ...(spec.basis ? { basis: spec.basis } : {}),
      ...(spec.origin ? { origin: spec.origin } : {}),
    },
    memory: {
      pivot: { rows: spec.rows, cols: spec.cols },
      sort: memorySort,
      ...(memoryLimit !== undefined ? { limit: memoryLimit } : {}),
      showAs: spec.showAs,
      totals: spec.totals,
    },
    rationale,
  };
}

/**
 * A listing's plan: everything in the query, nothing in the browser.
 *
 * There is no projection to be after, so there is no operation that could only
 * run once the rows arrive — every sort is exact, the whole chain goes down,
 * and the PAGE composes with it because the order is decided in the same place
 * the page is taken. That is also why a listing pages and a pivot does not: an
 * offset is only meaningful against an order the query itself promised.
 *
 * `limit` here is the page size, not a ceiling on the answer — `offset` walks
 * the rest and the result carries `totalRows`. The `memory` half is present and
 * empty rather than optional, so a consumer reading `plan.memory.sort` gets an
 * empty list rather than a crash.
 */
function planDetail(spec: ReportSpec): QueryPlan {
  const orderBy = spec.rowSort.filter((sort) => spec.columns.includes(sort.by));
  const rationale: QueryPlan['rationale'] = [];
  for (const sort of spec.rowSort) {
    if (spec.columns.includes(sort.by)) continue;
    rationale.push({
      step: 'sort',
      stage: 'memory',
      because: `this listing does not select "${sort.by}", so it cannot order by it`,
    });
  }
  rationale.push({
    step: orderBy.length ? 'sort' : 'page',
    stage: 'sql',
    because: 'a listing aggregates nothing, so the order and the page are both exact in the query',
  });
  rationale.push({
    step: 'row count',
    stage: 'sql',
    because: 'counted over the same predicates as the page, so the pager cannot disagree with it',
  });

  return {
    sql: {
      model: spec.model,
      from: spec.from,
      to: spec.to,
      groupBy: [],
      measures: [],
      columns: spec.columns,
      filters: activeFilters(spec.filters),
      ...(orderBy.length ? { orderBy } : {}),
      // CLAMPED to the listing's own bound. The spec's `limit` is bounded by
      // the PIVOT ceiling in the schema, which is a different argument about a
      // different shape, and this endpoint reads the field off a request body.
      limit: Math.min(spec.limit ?? DEFAULT_DETAIL_LIMIT, MAX_DETAIL_LIMIT),
      ...(spec.offset ? { offset: spec.offset } : {}),
      // ONE switch, both flags — the renderer draws a single total row and the
      // builder's control sets the pair, so either one being on is the ask.
      ...(spec.totals.row || spec.totals.col ? { totals: true } : {}),
      ...(spec.basis ? { basis: spec.basis } : {}),
      ...(spec.origin ? { origin: spec.origin } : {}),
    },
    memory: {
      pivot: { rows: [], cols: [] },
      sort: [],
      showAs: {},
      totals: { row: false, col: false },
    },
    rationale,
  };
}

/**
 * A stable identity for the QUERY half of a plan.
 *
 * Two specs with the same key have the same answer, so toggling a chart type,
 * a total or a share must not re-run the aggregate. Serialised over a CANONICAL
 * form of the filters, so reordering the bar — or pausing a rule that never
 * reached the query — is not mistaken for a different question.
 */
export function planKey(plan: QueryPlan): string {
  const {
    model,
    from,
    to,
    groupBy,
    measures,
    columns,
    filters,
    orderBy,
    limit,
    offset,
    totals,
    basis,
    origin,
  } = plan.sql;
  // Canonical rather than as-written: reordering the bar, or pausing a rule
  // that never reached the query, is the same question and must hash the same.
  const canonical = canonicalFilters(filters);
  // `offset` belongs here: turning the page is a different query, and a key
  // that omitted it would serve page 2 from page 1's cached rows. So do the
  // model and the period, for the same reason — moving the date range to
  // August and getting July's numbers back under August's heading is the same
  // bug with a worse tell, because nothing on screen looks stale. And so does
  // `totals`, which is the only flag on this half that a reader can toggle:
  // left out, turning the total row on hands back the same rows with no total
  // in them, and the switch reads as broken.
  return JSON.stringify({
    model,
    from,
    to,
    groupBy,
    measures,
    columns,
    filters: canonical,
    orderBy,
    limit,
    offset,
    totals,
    basis,
    origin,
  });
}
